import { supabase } from '@/lib/supabase'
import { logger } from '@/utils/logger'
import { connectionManager } from '@/shared/services/connectionManager'
import type { VolunteerSlot } from '../types/slot'

type SlotAction = 'LOCK' | 'UNLOCK' | 'REGISTER' | 'CANCEL'

interface SlotActionResult {
  success: boolean
  error?: string
  action?: string
  data?: any
}

interface SlotUpdateListener {
  (slot: VolunteerSlot, action: string): void
}

class UnifiedSlotService {
  private listeners: Map<string, Set<SlotUpdateListener>> = new Map()
  private currentUserEmail: string | null = null
  private connectionUnsubscribe: (() => void) | null = null
  private broadcastChannel: BroadcastChannel | null = null

  constructor() {
    this.initializeRealtime()
    this.startLockCleanup()
    this.startPollingFallback()
    this.initializeBroadcastChannel()
  }

  private async initializeRealtime() {
    this.connectionUnsubscribe = connectionManager.addListener((isConnected) => {
      if (isConnected && !connectionManager.getChannelCount()) {
        this.createRealtimeChannel()
      }
    })

    await this.createRealtimeChannel()
  }

  private async createRealtimeChannel() {
    const success = await connectionManager.createChannel('volunteer_slots_unified', {
      table: 'volunteer_slots',
      schema: 'public',
      event: '*',
      onUpdate: (payload) => this.handleSlotUpdate(payload),
      onBroadcast: (data) => this.handleBroadcastUpdate(data)
    })

    if (success) {
      logger.info('Volunteer slots realtime channel created', {
        component: 'UnifiedSlotService'
      })
    } else {
      logger.error('Failed to create volunteer slots realtime channel', {
        component: 'UnifiedSlotService'
      })
    }
  }

  private handleBroadcastUpdate(data: { slot: VolunteerSlot; action: string }) {
    if (!data || !data.slot || !data.slot.id) return

    const slot = data.slot
    const slotListeners = this.listeners.get(slot.id)
    if (slotListeners && slotListeners.size > 0) {
      slotListeners.forEach(listener => {
        try {
          listener(slot, data.action)
        } catch (error) {
          logger.error('Broadcast listener error', {
            component: 'UnifiedSlotService',
            data: { slotId: slot.id, error }
          })
        }
      })
    }
  }

  private handleSlotUpdate(payload: any) {
    const slot = payload.new as VolunteerSlot
    const oldSlot = payload.old as VolunteerSlot
    const eventType = payload.eventType

    logger.info('Real-time update received', {
      component: 'UnifiedSlotService',
      data: {
        eventType,
        slotId: slot?.id || oldSlot?.id,
        date: slot?.date || oldSlot?.date,
        lockedBy: slot?.locked_by,
        wasLockedBy: oldSlot?.locked_by
      }
    })

    let action = 'UNKNOWN'
    if (eventType === 'UPDATE') {
      if (slot.volunteer_email && !oldSlot.volunteer_email) {
        action = 'REGISTERED'
      } else if (!slot.volunteer_email && oldSlot.volunteer_email) {
        action = 'CANCELLED'
      } else if (slot.locked_by && !oldSlot.locked_by) {
        action = 'LOCKED'
      } else if (!slot.locked_by && oldSlot.locked_by) {
        action = 'UNLOCKED'
      } else {
        action = 'UPDATED'
      }
    }
    
    const slotId = slot?.id || oldSlot?.id
    if (slotId) {
      const slotListeners = this.listeners.get(slotId)

      if (this.broadcastChannel) {
        try {
          this.broadcastChannel.postMessage({
            type: 'SLOT_UPDATE',
            slot: slot || oldSlot,
            action
          })
        } catch (error) {
          logger.error('Failed to broadcast to other tabs', {
            component: 'UnifiedSlotService',
            data: { slotId, error }
          })
        }
      }

      if (slotListeners && slotListeners.size > 0) {
        slotListeners.forEach(listener => {
          try {
            listener(slot || oldSlot, action)
          } catch (error) {
            logger.error('Slot listener error', {
              component: 'UnifiedSlotService',
              data: { slotId, error }
            })
          }
        })
      }
    }
  }

  async performSlotAction(
    slotId: string,
    action: SlotAction,
    userName?: string,
    email?: string,
    phoneNumber?: string,
    siblingClasses?: string[]
  ): Promise<SlotActionResult> {
    let userEmail = this.currentUserEmail

    if (!userEmail) {
      const { data: { user } } = await supabase.auth.getUser()
      userEmail = user?.email || null

      if (userEmail) {
        this.currentUserEmail = userEmail
      }
    }

    if (!userEmail) {
      logger.error('User not authenticated', {
        component: 'UnifiedSlotService',
        action
      })
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const { data, error } = await supabase.rpc('manage_volunteer_slot', {
        slot_id: slotId,
        action_type: action,
        user_email: userEmail,
        user_name: userName || userEmail.split('@')[0]
      })

      if (error) {
        return { success: false, error: error.message }
      }

      if (!data.success) {
        return { success: false, error: data.error }
      }

      if (action === 'LOCK') {
        const expiresAt = data.expires_at || new Date(Date.now() + 60000).toISOString()
        this.notifySlotUpdate(slotId, {
          locked_by: userEmail,
          locked_at: new Date().toISOString(),
          lock_expires_at: expiresAt,
          last_action: 'LOCK',
          last_action_by: userEmail,
          last_action_at: new Date().toISOString()
        }, 'LOCKED')
      } else if (action === 'UNLOCK') {
        this.notifySlotUpdate(slotId, {
          locked_by: null,
          locked_at: null,
          lock_expires_at: null,
          last_action: 'UNLOCK',
          last_action_by: userEmail,
          last_action_at: new Date().toISOString()
        }, 'UNLOCKED')
      } else if (action === 'REGISTER') {
        const updatePayload: Record<string, any> = {}
        if (phoneNumber) {
          updatePayload.volunteer_phone = phoneNumber
        }
        if (siblingClasses && siblingClasses.length > 0) {
          updatePayload.sibling_classes = siblingClasses
          updatePayload.has_siblings = true
        } else {
          updatePayload.sibling_classes = []
          updatePayload.has_siblings = false
        }

        await supabase
          .from('volunteer_slots')
          .update(updatePayload)
          .eq('id', slotId)

        this.notifySlotUpdate(slotId, {
          volunteer_name: userName || userEmail.split('@')[0],
          volunteer_email: userEmail,
          volunteer_phone: phoneNumber || null,
          sibling_classes: siblingClasses || [],
          has_siblings: !!(siblingClasses && siblingClasses.length > 0),
          locked_by: null,
          locked_at: null,
          lock_expires_at: null,
          last_action: 'REGISTER',
          last_action_by: userEmail,
          last_action_at: new Date().toISOString()
        }, 'REGISTERED')
      } else if (action === 'CANCEL') {
        this.notifySlotUpdate(slotId, {
          volunteer_name: null,
          volunteer_email: null,
          volunteer_phone: null,
          sibling_classes: [],
          has_siblings: false,
          locked_by: null,
          locked_at: null,
          lock_expires_at: null,
          last_action: 'CANCEL',
          last_action_by: userEmail,
          last_action_at: new Date().toISOString()
        }, 'CANCELLED')
      }

      return { success: true, data }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      }
    }
  }

  notifySlotUpdate(slotId: string, partial: Partial<VolunteerSlot>, action: string) {
    const slotListeners = this.listeners.get(slotId)
    const slotData = { id: slotId, ...partial } as VolunteerSlot

    // 1. Instant WebSocket broadcast across ALL devices & browsers (sub-50ms)
    connectionManager.sendBroadcast('volunteer_slots_unified', 'SLOT_UPDATE', {
      slot: slotData,
      action
    })

    // 2. BroadcastChannel to all tabs on same device
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage({
          type: 'SLOT_UPDATE',
          slot: slotData,
          action
        })
      } catch (error) {
        logger.error('Failed to broadcast to other tabs', {
          component: 'UnifiedSlotService',
          data: { slotId, error }
        })
      }
    }

    if (slotListeners && slotListeners.size > 0) {
      slotListeners.forEach(listener => {
        try {
          listener(slotData, action)
        } catch (error) {
          logger.error('Slot listener error', {
            component: 'UnifiedSlotService',
            data: { slotId, error }
          })
        }
      })
    }
  }

  async lockSlot(slotId: string): Promise<SlotActionResult> {
    return this.performSlotAction(slotId, 'LOCK')
  }

  async unlockSlot(slotId: string): Promise<SlotActionResult> {
    return this.performSlotAction(slotId, 'UNLOCK')
  }

  async registerForSlot(
    slotId: string,
    userName: string,
    phoneNumber: string,
    email?: string,
    siblingClasses?: string[]
  ): Promise<SlotActionResult> {
    return this.performSlotAction(slotId, 'REGISTER', userName, email, phoneNumber, siblingClasses)
  }

  async cancelRegistration(slotId: string): Promise<SlotActionResult> {
    return this.performSlotAction(slotId, 'CANCEL')
  }

  subscribe(slotId: string, listener: SlotUpdateListener): () => void {
    if (!this.listeners.has(slotId)) {
      this.listeners.set(slotId, new Set())
    }
    this.listeners.get(slotId)!.add(listener)

    return () => {
      const slotListeners = this.listeners.get(slotId)
      if (slotListeners) {
        slotListeners.delete(listener)
        if (slotListeners.size === 0) {
          this.listeners.delete(slotId)
        }
      }
    }
  }

  setCurrentUser(email: string | null) {
    this.currentUserEmail = email
  }

  private startLockCleanup() {
    setInterval(async () => {
      try {
        await supabase.rpc('cleanup_expired_slot_locks')
      } catch (error) {
        logger.error('Lock cleanup failed', {
          component: 'UnifiedSlotService',
          data: error
        })
      }
    }, 30000)
  }

  private initializeBroadcastChannel() {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return

    try {
      this.broadcastChannel = new BroadcastChannel('volunteer_slots_sync')
      this.broadcastChannel.onmessage = (event) => {
        const { type, slot, action } = event.data

        if (type === 'SLOT_UPDATE') {
          const slotListeners = this.listeners.get(slot?.id)
          if (slotListeners && slotListeners.size > 0) {
            slotListeners.forEach(listener => {
              try {
                listener(slot, action)
              } catch (error) {
                logger.error('BroadcastChannel listener error', {
                  component: 'UnifiedSlotService',
                  data: { slotId: slot?.id, error }
                })
              }
            })
          }
        }
      }
    } catch (error) {
      logger.error('Failed to initialize BroadcastChannel', {
        component: 'UnifiedSlotService',
        data: error
      })
    }
  }

  private startPollingFallback() {
    setInterval(async () => {
      if (this.listeners.size === 0) return

      try {
        const slotIds = Array.from(this.listeners.keys())
        if (slotIds.length === 0) return

        const { data: slots, error } = await supabase
          .from('volunteer_slots')
          .select('*')
          .in('id', slotIds)

        if (error || !slots) return

        slots.forEach(slot => {
          const slotListeners = this.listeners.get(slot.id)
          if (slotListeners && slotListeners.size > 0) {
            slotListeners.forEach(listener => {
              try {
                listener(slot as VolunteerSlot, 'POLLING_UPDATE')
              } catch (error) {
                logger.error('Polling listener error', {
                  component: 'UnifiedSlotService',
                  data: { slotId: slot.id, error }
                })
              }
            })
          }
        })
      } catch (error) {
        logger.error('Polling fallback exception', {
          component: 'UnifiedSlotService',
          data: error
        })
      }
    }, 8000)
  }

  isRealtimeConnected(): boolean {
    return connectionManager.isRealtimeConnected()
  }

  destroy() {
    if (this.connectionUnsubscribe) {
      this.connectionUnsubscribe()
      this.connectionUnsubscribe = null
    }
    connectionManager.removeChannel('volunteer_slots_unified')
    this.listeners.clear()
  }
}

export const unifiedSlotService = new UnifiedSlotService()
export { UnifiedSlotService }

supabase.auth.onAuthStateChange((event, session) => {
  unifiedSlotService.setCurrentUser(session?.user?.email || null)
})

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    unifiedSlotService.destroy()
  })
}
